import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, InputNumber, Popover, Select, Space, Typography } from "antd";
import { DeleteOutlined, SplitCellsOutlined } from "@ant-design/icons";
import { SketchPicker } from "react-color";
import { PermissionOverlay } from "@components/PermissionedComponents";
import { PRESET_COLORS } from "@components/Settings/OverviewSettings/ColorPickerWithLabel";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { updateAcwrThresholdConfig } from "@store/actions/team.actions";
import { DEFAULT_ACWR_THRESHOLD_CONFIG } from "@utils/acwr_utils";

const MIN_SEGMENTS = 3;
const MAX_SEGMENTS = 5;
const THRESHOLD_MIN = 0;
const THRESHOLD_MAX = 3;
const THRESHOLD_STEP = 0.05;
const SPAN = THRESHOLD_MAX - THRESHOLD_MIN;

// Work in integer hundredths, so a snapped value never carries float noise.
const snapToStep = (value) => {
  const stepHundredths = Math.round(THRESHOLD_STEP * 100);
  const hundredths = Math.round(value * 100);
  return (Math.round(hundredths / stepHundredths) * stepHundredths) / 100;
};

const roundToHundredths = (value) => Math.round(value * 100) / 100;

const HELP_TEXT =
  "Each segment is an ACWR range from 0 to 3. The first segment raises the " +
  "underworked flag, and the last segment raises the overworked flag. Click a " +
  "segment to change its color, to split it, or to remove it. Drag a boundary " +
  "marker to move it, or double-click the marker to type a value.";

/**
 * A compact editor for the team ACWR threshold config. The bar is the whole
 * control: segments open a popover, and boundary markers drag or take typed
 * input. Every change keeps the config valid, so there is no error state.
 * The config keeps a segment_names list for the backend schema, but the
 * editor never shows it.
 */
export const ACWRThresholdConfig = () => {
  const dispatch = useDispatch();
  const barRef = useRef(null);
  const editCancelledRef = useRef(false);

  // No destructuring default here: a fresh `{}` per render would retrigger the
  // sync effect below forever whenever the store field is undefined.
  const {
    info: { acwr_threshold_config }
  } = useSelector(({ team }) => team);

  const [config, setConfig] = useState(() => ({ ...DEFAULT_ACWR_THRESHOLD_CONFIG, ...acwr_threshold_config }));
  const [dragIndex, setDragIndex] = useState(null);
  const [openSegment, setOpenSegment] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState(null);

  useEffect(() => {
    setConfig({ ...DEFAULT_ACWR_THRESHOLD_CONFIG, ...acwr_threshold_config });
  }, [acwr_threshold_config]);

  const thresholdBounds = (index, thresholds) => ({
    minValue: index === 0 ? THRESHOLD_MIN + THRESHOLD_STEP : thresholds[index - 1] + THRESHOLD_STEP,
    maxValue: index === thresholds.length - 1
      ? THRESHOLD_MAX - THRESHOLD_STEP
      : thresholds[index + 1] - THRESHOLD_STEP,
  });

  const segmentRange = (index) => ({
    lower: index === 0 ? THRESHOLD_MIN : config.thresholds[index - 1],
    upper: index === config.segments - 1 ? THRESHOLD_MAX : config.thresholds[index],
  });

  const setThreshold = (index, value) => {
    const { minValue, maxValue } = thresholdBounds(index, config.thresholds);
    const newThresholds = [...config.thresholds];
    const clamped = Math.max(minValue, Math.min(maxValue, snapToStep(value)));
    newThresholds[index] = roundToHundredths(clamped);
    setConfig({ ...config, thresholds: newThresholds });
  };

  // Drag: markers follow the pointer through document-level listeners, so a
  // fast drag that leaves the bar keeps working.
  useEffect(() => {
    if (dragIndex === null) return undefined;
    const handleMove = (e) => {
      if (!barRef.current) return;
      const rect = barRef.current.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setThreshold(dragIndex, THRESHOLD_MIN + fraction * SPAN);
    };
    const handleUp = () => setDragIndex(null);
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
      document.body.style.userSelect = "";
    };
  }, [dragIndex, config]);

  const startEditing = (index) => {
    editCancelledRef.current = false;
    setEditingIndex(index);
    setEditingValue(Number(config.thresholds[index].toFixed(2)));
  };

  const commitEdit = () => {
    if (editingIndex === null) return;
    if (!editCancelledRef.current && editingValue !== null && Number.isFinite(Number(editingValue))) {
      setThreshold(editingIndex, Number(editingValue));
    }
    setEditingIndex(null);
  };

  const handleColorChange = (index, hex) => {
    const newColors = [...config.colors];
    newColors[index] = hex;
    setConfig({ ...config, colors: newColors });
  };

  const splitSegment = (index) => {
    const { lower, upper } = segmentRange(index);
    const newThresholds = [...config.thresholds];
    newThresholds.splice(index, 0, snapToStep((lower + upper) / 2));
    const newColors = [...config.colors];
    newColors.splice(index + 1, 0, config.colors[index]);
    const newNames = [...config.segment_names];
    newNames.splice(index + 1, 0, config.segment_names[index]);
    setConfig({
      ...config,
      segments: config.segments + 1,
      colors: newColors,
      segment_names: newNames,
      thresholds: newThresholds,
    });
    setOpenSegment(null);
  };

  const removeSegment = (index) => {
    const newColors = [...config.colors];
    newColors.splice(index, 1);
    const newNames = [...config.segment_names];
    newNames.splice(index, 1);
    const newThresholds = [...config.thresholds];
    // The segment to the left absorbs the space, so drop the lower boundary.
    newThresholds.splice(Math.max(index - 1, 0), 1);
    setConfig({
      ...config,
      segments: config.segments - 1,
      colors: newColors,
      segment_names: newNames,
      thresholds: newThresholds,
    });
    setOpenSegment(null);
  };

  // New thresholds land strictly between the second-to-last and the last
  // threshold, so growing needs one free step-grid point per insertion there.
  const insertionZone = (thresholds) => ({
    lower: thresholds.length >= 2 ? thresholds[thresholds.length - 2] : THRESHOLD_MIN,
    upper: thresholds[thresholds.length - 1],
  });

  const canGrowTo = (segments) => {
    const extraThresholds = segments - 1 - config.thresholds.length;
    if (extraThresholds <= 0) return true;
    const { lower, upper } = insertionZone(config.thresholds);
    return Math.round((upper - lower) * 100) >= (extraThresholds + 1) * Math.round(THRESHOLD_STEP * 100);
  };

  // The dropdown grows and shrinks at the same place as Split and Remove:
  // in front of the last segment, so the outer flag boundaries do not move.
  const handleSegmentCountChange = (segments) => {
    const middleIndex = Math.floor((DEFAULT_ACWR_THRESHOLD_CONFIG.colors.length - 1) / 2);
    const newColors = [...config.colors];
    while (newColors.length < segments) {
      newColors.splice(newColors.length - 1, 0, DEFAULT_ACWR_THRESHOLD_CONFIG.colors[middleIndex]);
    }
    newColors.splice(segments - 1, newColors.length - segments);

    const newNames = [...config.segment_names];
    while (newNames.length < segments) {
      newNames.splice(newNames.length - 1, 0, DEFAULT_ACWR_THRESHOLD_CONFIG.segment_names[middleIndex]);
    }
    newNames.splice(segments - 1, newNames.length - segments);

    const newThresholds = [...config.thresholds];
    const extraThresholds = segments - 1 - newThresholds.length;
    if (extraThresholds > 0) {
      const { lower, upper } = insertionZone(newThresholds);
      const gap = (upper - lower) / (extraThresholds + 1);
      const inserted = [];
      let previous = lower;
      for (let i = 1; i <= extraThresholds; i += 1) {
        // Snap to the step grid, but keep the whole list strictly ascending:
        // at least a step above the previous value, and low enough that the
        // remaining insertions still fit under the anchored last threshold.
        const snapped = snapToStep(lower + gap * i);
        const floor = roundToHundredths(previous + THRESHOLD_STEP);
        const ceiling = roundToHundredths(upper - THRESHOLD_STEP * (extraThresholds - i + 1));
        const value = Math.min(Math.max(snapped, floor), ceiling);
        inserted.push(value);
        previous = value;
      }
      newThresholds.splice(newThresholds.length - 1, 0, ...inserted);
    } else {
      newThresholds.splice(segments - 2, newThresholds.length - (segments - 1));
    }

    setConfig({
      ...config,
      segments,
      colors: newColors,
      segment_names: newNames,
      thresholds: newThresholds,
    });
  };

  const handleSave = async () => {
    try {
      await dispatch(updateAcwrThresholdConfig({ acwr_threshold_config: config }));
      message.success("ACWR thresholds saved successfully");
    } catch (error) {
      message.error(typeof error === "string" ? error : "Error saving ACWR thresholds");
    }
  };

  const canSplit = (index) => {
    if (config.segments >= MAX_SEGMENTS) return false;
    const { lower, upper } = segmentRange(index);
    return upper - lower >= 2 * THRESHOLD_STEP;
  };

  const segmentPopover = (index) => (
    <Space direction="vertical" size="small">
      <SketchPicker
        color={config.colors[index]}
        onChange={(newColor) => handleColorChange(index, newColor.hex)}
        disableAlpha
        presetColors={PRESET_COLORS}
        styles={{ default: { picker: { boxShadow: "none", padding: 0, width: 220 } } }}
      />
      <Space>
        <Button
          size="small"
          icon={<SplitCellsOutlined />}
          disabled={!canSplit(index)}
          onClick={() => splitSegment(index)}
        >
          Split
        </Button>
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={config.segments <= MIN_SEGMENTS}
          onClick={() => removeSegment(index)}
        >
          Remove
        </Button>
      </Space>
    </Space>
  );

  const segmentWidth = (index) => {
    const { lower, upper } = segmentRange(index);
    return ((upper - lower) / SPAN) * 100;
  };

  return (
    <div>
      <strong>ACWR Thresholds</strong>
      <PermissionOverlay
        permissionCheck="EditGeneralTeamSettings"
        tooltipMessage="You don't have permission to edit general team settings"
      >
        <Space direction="vertical" size="middle" style={{ width: "100%", marginTop: 16 }}>
          <div
            ref={barRef}
            style={{
              position: "relative",
              display: "flex",
              height: 44,
              border: "1px solid #d9d9d9",
              borderRadius: 6,
              overflow: "hidden",
              cursor: dragIndex !== null ? "grabbing" : "default",
            }}
          >
            {config.colors.map((color, index) => (
              <Popover
                key={index}
                open={openSegment === index}
                onOpenChange={(open) => setOpenSegment(open ? index : null)}
                trigger="click"
                placement="bottom"
                styles={{ body: { padding: 8 } }}
                content={segmentPopover(index)}
              >
                <div
                  className="acwr-segment"
                  title="Click to edit this segment"
                  style={{
                    backgroundColor: color,
                    width: `${segmentWidth(index)}%`,
                    cursor: "pointer",
                    border: "1px solid #fff",
                    borderLeft: index === 0 ? "none" : "1px solid #fff",
                    borderRight:
                      index === config.segments - 1 ? "none" : "1px solid #fff",
                  }}
                />
              </Popover>
            ))}

            {config.thresholds.map((threshold, index) => {
              const left = ((threshold - THRESHOLD_MIN) / SPAN) * 100;
              return (
                <div
                  key={`threshold-${index}`}
                  style={{
                    position: "absolute",
                    left: `${left}%`,
                    top: 0,
                    bottom: 0,
                    transform: "translateX(-50%)",
                    display: "flex",
                    alignItems: "center",
                    zIndex: 2,
                  }}
                >
                  {editingIndex === index ? (
                    <InputNumber
                      size="small"
                      autoFocus
                      min={THRESHOLD_MIN}
                      max={THRESHOLD_MAX}
                      step={THRESHOLD_STEP}
                      value={editingValue}
                      onChange={setEditingValue}
                      onPressEnter={commitEdit}
                      onBlur={commitEdit}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          editCancelledRef.current = true;
                          e.target.blur();
                        }
                      }}
                      style={{ width: 72 }}
                    />
                  ) : (
                    <div
                      title="Drag to move. Double-click to type a value."
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setDragIndex(index);
                      }}
                      onDoubleClick={() => startEditing(index)}
                      style={{
                        background: "#fff",
                        border: "2px solid #1677ff",
                        borderRadius: 12,
                        padding: "0 8px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#1677ff",
                        cursor: dragIndex === index ? "grabbing" : "grab",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                      }}
                    >
                      {threshold.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Typography.Text disabled className="description">
            {HELP_TEXT}
          </Typography.Text>

          <Space>
            <Typography.Text type="secondary">Segments:</Typography.Text>
            <Select
              size="small"
              value={config.segments}
              onChange={handleSegmentCountChange}
              options={[3, 4, 5].map((n) => ({ value: n, label: n, disabled: !canGrowTo(n) }))}
              style={{ width: 56 }}
            />
            <Button type="primary" onClick={handleSave}>
              Save
            </Button>
            <Button onClick={() => setConfig({ ...DEFAULT_ACWR_THRESHOLD_CONFIG })}>
              Reset to Default
            </Button>
          </Space>
        </Space>
      </PermissionOverlay>
    </div>
  );
};
